import React from 'react';
import { Link } from 'react-router-dom';

import {
  CUSTOM_SLIDE_LIST_ROUTE,
  IF_VIEWER_URL,
  ORDERS_SLIDE_LIST_URL,
  SLIDE_VIEWER_URL,
} from 'constants/urls';
import { getNormalizedSlideFileName } from 'utilities/slides';
import { Grid, Typography } from '@mui/material';
import {
  gridStyle,
  tabPadding,
  typographySlideNameStyle,
} from 'components/OrderDetails/orderDetailsSlidesStyles';
import {
  downloadMultiIFSlideSourceByOMETIFF,
  downloadMultiIFSlideSourceByQPTIFF,
} from 'services/resources/slides';
import { backendURL } from 'services/backendAPI';
import {
  useLazyDownloadIFSlideSourceOMETiffQuery,
  useLazyDownloadIFSlideSourceQPTiffQuery,
} from 'store/apis/downloadSlideApi';
import { isMatchingFile } from 'components/ImageViewer/SlideViewerHeader/utils';
import {
  NO_MATCHING_SLIDES_MESSAGE,
  UPLOAD_ANNOTATIONS_SUCCESS_MESSAGE,
} from 'components/ImageViewer/constants';
import { ANNOTATION_PROVIDERS } from 'components/OrderSlidesList/ExtAnnotations/constants';

const ifSlideStyles = {
  WebkitFilter: 'invert(100%)',
  filter: 'invert(100%)',
  maxHeight: '500px',
};

export const getIFViewerURLFromSlide = ({ ifSlide }) => {
  const { uuid: slideUUID } = ifSlide;
  const { uuid: orderUUID } = ifSlide.order;

  let url = IF_VIEWER_URL.replace(':orderUUID', orderUUID);
  url = url.replace(':slideUUID', slideUUID);

  return url;
};

export const RenderSlideMacro = ({ slide, quickFilterText }) => {
  const slideViewerURL = SLIDE_VIEWER_URL.replace(':slideUUID', slide.uuid);
  const slideName = slide.name;

  const serializedSlideName = getNormalizedSlideFileName(slide);

  if (
    quickFilterText &&
    !slideName.toUpperCase().includes(quickFilterText.toUpperCase())
  ) {
    return null;
  }

  return (
    <Grid item xl={2} lg={3} md={4} xs={6} key={slide.uuid} style={gridStyle}>
      <Link to={slideViewerURL}>
        <Typography style={typographySlideNameStyle} variant="h6">
          {serializedSlideName}
        </Typography>
        <img
          src={slide.medium_macro_url}
          loading="lazy"
          width="60%"
          alt="Macro"
        />
      </Link>
    </Grid>
  );
};

export const RenderIFSlideMacro = ({ ifSlide }) => {
  const serializedSlideName = getNormalizedSlideFileName(ifSlide);
  const viewerURL = getIFViewerURLFromSlide({ ifSlide });

  return (
    <Grid item xl={2} md={3} sm={3} xs={4} key={ifSlide.uuid} style={gridStyle}>
      <Link to={viewerURL}>
        <Typography style={typographySlideNameStyle} variant="h6">
          {serializedSlideName}
        </Typography>
      </Link>
      <img
        src={ifSlide.gcp_macro_medium}
        width="60%"
        alt="Macro"
        style={ifSlideStyles}
      />
    </Grid>
  );
};

export const TabContainer = ({ children }) => (
  <Typography component="div" style={tabPadding}>
    {children}
  </Typography>
);

export const DefaultTabWrapper = ({ children }) => (
  <Grid container spacing={2}>
    {children}
  </Grid>
);

export const useGetSingleContextMenuItemsIFGrid = () => {
  const [downloadIFSlideSourceQPTiff] =
    useLazyDownloadIFSlideSourceQPTiffQuery();
  const [downloadIFSlideSourceOMETiff] =
    useLazyDownloadIFSlideSourceOMETiffQuery();

  const getSingleContextMenuItemsIFGrid = ({ row, isStaff }) => {
    const menuItems = [
      {
        name: 'Copy Slide Name',
        action: () => {
          if (row) {
            navigator.clipboard.writeText(row.name);
          }
        },
      },
      {
        name: 'Download Slide (qptiff)',
        action: () => {
          if (row) {
            downloadIFSlideSourceQPTiff({ IFSlide: row });
          }
        },
      },
      {
        name: 'Download Slide (ome.tiff)',
        action: () => {
          if (row) {
            downloadIFSlideSourceOMETiff({ IFSlide: row });
          }
        },
      },
    ];

    if (isStaff) {
      const lisaSlideLink = `${backendURL}/lisa/core/ifslide/${row.id}/change/`;

      const additionalAdmin = [
        {
          name: 'View Slide (LISA)',
          action: () => {
            if (row) {
              window.open(lisaSlideLink, '_blank');
            }
          },
        },
      ];

      menuItems.push(...additionalAdmin);

      return menuItems;
    }

    return menuItems;
  };

  return getSingleContextMenuItemsIFGrid;
};

export const getMultiContextMenuItemsIFViewer = ({ rows }) => {
  const menuContext = [
    {
      name: 'Download Slides as .qptiff',
      action: () => {
        downloadMultiIFSlideSourceByQPTIFF({ slides: rows });
      },
    },
    {
      name: 'Download Slides as .ome.tiff',
      action: () => {
        downloadMultiIFSlideSourceByOMETIFF({ slides: rows });
      },
    },
  ];

  return menuContext;
};

export const viewAllSlides = ({ slides, selectedRows }) => {
  if (!slides?.length) return;

  const pageUrl = `${ORDERS_SLIDE_LIST_URL.replace(
    ':orderUUID',
    slides[0].order?.uuid,
  )}#`;

  if (selectedRows?.length) {
    const query = selectedRows.map((row) => `slideId=${row.uuid}`).join(';');

    const url = `${pageUrl}${query}`;
    window.open(url);

    return;
  }

  window.open(pageUrl);
};

export const viewSelectedSlides = ({ selectedRows, openModel }) => {
  if (selectedRows?.length) {
    const slidesUUIDs = selectedRows.map((row) => row.uuid);
    localStorage.setItem('customGroupUUIDs', JSON.stringify(slidesUUIDs));

    if (openModel) {
      localStorage.setItem('customGroupOpenModel', openModel);

      const query = selectedRows.map((row) => `slideId=${row.uuid}`).join(';');
      const url = `${CUSTOM_SLIDE_LIST_ROUTE}#${query}`;

      window.open(url);

      return;
    }

    window.open(CUSTOM_SLIDE_LIST_ROUTE);
  }
};

export const findMatchedSlides = (files, slides) =>
  slides
    .filter(
      (slide) =>
        files.some((file) => isMatchingFile(slide.name, file.fileName)) &&
        slide.is_public,
    )
    .reduce((acc, slide) => {
      const existing = acc.find((s) => s.name === slide.name);
      if (!existing) {
        acc.push(slide);
      } else if (existing.created < slide.created) {
        const existingIndex = acc.indexOf(existing);
        acc[existingIndex] = slide;
      }

      return acc;
    }, []);

export const handleBatchUploadAnnotationsAttachment = async ({
  filesToUpload,
  slides,
  handleClose,
  showInfo,
  uploadSlideAnnotation,
  showSuccess,
}) => {
  const matchedFiles = filesToUpload.filter((file) =>
    slides.some((slide) => isMatchingFile(slide.name, file.fileName)),
  );
  if (!matchedFiles.length) {
    showInfo(NO_MATCHING_SLIDES_MESSAGE);

    return;
  }
  const matchedSlidesList = findMatchedSlides(matchedFiles, slides);
  const files = matchedFiles.map(({ file, fileName, type }) => ({
    file,
    type,
    slideUUID: matchedSlidesList.find((slide) =>
      isMatchingFile(slide.name, fileName),
    ).uuid,
  }));

  const promises = files.map(async ({ file, type, slideUUID }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('provider', type);
    formData.append('slide_uuid', slideUUID);
    await uploadSlideAnnotation(formData).unwrap();
  });

  await Promise.all(promises);
  showSuccess(UPLOAD_ANNOTATIONS_SUCCESS_MESSAGE);
  handleClose();
};

export const getAnnotationsByProvider = (slide, uploadedAnnotations) => {
  if (!uploadedAnnotations) return [];

  return uploadedAnnotations
    .filter((annotation) => annotation.slide.uuid === slide.uuid)
    .reduce((uniqueProviders, annotation) => {
      const provider = ANNOTATION_PROVIDERS.find(
        (p) => p.provider === annotation.provider.toLowerCase(),
      );

      const existingProvider = uniqueProviders.find(
        (up) => up.provider === provider?.provider,
      );

      if (provider) {
        if (existingProvider) {
          existingProvider.count += 1;
        } else {
          uniqueProviders.push({
            ...provider,
            count: 1,
          });
        }
      }

      return uniqueProviders;
    }, []);
};
